/* Sync — §6. Two devices, no automatic merge.
 *
 * The whole book is serialised, encrypted in the browser, and pushed to the
 * Worker as one opaque blob. Last write wins, but never silently: before
 * overwriting a copy newer than the one this device started from, the caller has
 * to confirm. */

import * as api from '../api/client';
import { decrypt, encrypt } from './crypto';
import { saveTextFile, type Saved } from './exportFile';
import type { BookData } from '../data/db';

const FORMAT = 1;

export interface ExportFile {
  format: number;
  exportedAt: string;
  book: BookData;
}

export function serialise(book: BookData): string {
  const payload: ExportFile = { format: FORMAT, exportedAt: new Date().toISOString(), book };
  return JSON.stringify(payload, null, 2);
}

export function parse(text: string): BookData {
  const parsed = JSON.parse(text) as Partial<ExportFile> & Partial<BookData>;
  // Accept both the wrapped export and a bare book object, so a file hand-edited
  // down to its contents still imports.
  const book = (parsed as ExportFile).book ?? (parsed as BookData);
  // 'accounts' is deliberately absent: a file exported before brokers became
  // editable is still a valid book, and replaceBook fills in the originals.
  const required: Array<keyof BookData> = [
    'holdings', 'theses', 'trades', 'cashflows', 'snapshots', 'perps', 'cash', 'themes', 'caps',
  ];
  for (const key of required) {
    if (!Array.isArray(book[key])) {
      throw new Error(`This file is missing "${key}", so it is not a book export.`);
    }
  }
  return book;
}

export type PushOutcome =
  | { status: 'saved'; updatedAt: string }
  | { status: 'conflict'; theirsAt: string };

/** Encrypts and pushes. `seenAt` is the stamp this device last pulled or wrote;
    a mismatch means the other device got there first. */
export async function push(
  book: BookData,
  passphrase: string,
  seenAt: string | null,
  opts: { force?: boolean; token?: string } = {},
): Promise<PushOutcome> {
  const envelope = await encrypt(serialise(book), passphrase);
  const result = await api.putBlob(envelope, {
    seenAt,
    ...(opts.force !== undefined ? { force: opts.force } : {}),
    ...(opts.token !== undefined ? { token: opts.token } : {}),
  });
  return result.ok
    ? { status: 'saved', updatedAt: result.updatedAt }
    : { status: 'conflict', theirsAt: result.conflictAt };
}

export interface PullResult {
  book: BookData;
  updatedAt: string | null;
}

/** Pulls and decrypts. A wrong passphrase throws WrongPassphrase and nothing on
    this device is touched. */
export async function pull(passphrase: string, token?: string): Promise<PullResult | null> {
  const stored = await api.getBlob(token);
  if (!stored) return null;
  const plaintext = await decrypt(stored.blob, passphrase);
  return { book: parse(plaintext), updatedAt: stored.updatedAt };
}

/** The name the export goes out under. One a day, so a second export on the
    same day replaces the first rather than accumulating. */
export function exportName(now = new Date()): string {
  return `position-book-${now.toISOString().slice(0, 10)}.json`;
}

/** Writes the plaintext export. This is the real backup: if the passphrase is
    lost, the blob is unrecoverable and this file is all there is — which is why
    it goes through `lib/exportFile.ts` rather than clicking an anchor here. In
    the macOS shell an anchor did nothing at all, silently, for exactly the file
    the app tells you to keep. */
export function downloadExport(book: BookData): Promise<Saved> {
  return saveTextFile(exportName(), serialise(book));
}
